<template>
<div class="add-funds-to-series-final">
  <div class="add-funds-to-series-final__series-info">
    <h4 class="add-funds-to-series-final__series-info-name">
      {{ selectedSeries.seriesName }}
    </h4>
    <mds-button
      type="button"
      class="button__edit-series-name"
      variation="flat"
      @click="goToEditSeriesName"
    >Edit Series Name</mds-button>
  </div>
  <div class="add-funds-to-series-final__funds-selection">
    <!-- Available Funds -->
    <series-available-funds-to-be-added
      class="add-funds-to-series-final__filter-funds"
      :selected-client="selectedClient"
      :selected-funds="selectedFundsList"
      :series="selectedSeries"
      @changed-fund-selection="handleChangeFundSelection"
    ></series-available-funds-to-be-added>
    <!-- Selected Funds -->
    <series-selected-funds
      :series="selectedSeries"
      :selected-funds="selectedFundsList"
      :original-funds="originalFundsList"
      :removed-funds="removedFundsList"
      :selectedFiduciaryClientId="selectedClient.id"
      :is-loading="isLoading"
      :first-selected-fund="selectedFund"
      @fund-removed="handleFundRemoved"
    ></series-selected-funds>
  </div>
</div>
</template>

<script>
import {
  sortBy,
  concat,
  uniqBy,
  cloneDeep,
} from 'lodash';
import { mapActions, mapState } from 'vuex';

import SeriesSelectedFunds from '@/components/organisms/SeriesSelectedFunds';
import SeriesAvailableFundsToBeAdded from '@/components/organisms/SeriesAvailableFundsToBeAdded';
import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';
import * as TargetSeries from '@/services/TargetSeries';

/**
 * @Component(SeriesFundsFinalSelection)
 */
export default {
  components: {
    SeriesSelectedFunds,
    SeriesAvailableFundsToBeAdded,
  },
  mixins: [
    ErrorNotificationMixin({ component: 'SeriesFundsFinalSelection' }),
  ],
  props: {
    componentData: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      selectedFundsList: [],
      originalFundsList: [],
      removedFundsList: [],
    };
  },
  created() {
    if (this.selectedSeries?.manageSeries) {
      this.loadSeriesFunds();
    } else {
      if (!this.selectedSeries?.isNew
        && this.selectedSeries?.belongsToSelectedClient
        && this.isEmptyTheSavedSelectedFundsList
      ) {
        return this.loadSeriesFunds();
      }

      this.setIsLoading(false);

      if (!this.isEmptyTheSavedSelectedFundsList) {
        this.selectedFundsList = this.savedSelectedFundsList;
        this.originalFundsList = this.savedOriginalFundsList;
        this.removedFundsList = this.savedRemovedFundsList;
      } else {
        this.selectedFundsList.unshift(this.selectedFund);
      }
    }

    return this.selectedFundsList;
  },
  methods: {
    ...mapActions([
      'setIsLoading',
      'openNotification',
      'saveSelectedFundsList',
      'saveOriginalFundsList',
      'saveRemovedFundsList',
    ]),
    goToEditSeriesName() {
      if (!this.selectedSeries.isNew) {
        this.$emit('component-action', {
          name: 'go-to-edit-series-name',
          selectedSeries: this.selectedSeries,
        });
        return 'edit-existent';
      }
      this.$emit('component-action', {
        name: 'go-to-create-new-series',
        selectedSeries: this.selectedSeries,
      });
      return 'edit-new';
    },
    loadSeriesFunds() {
      this.setIsLoading(true);
      const dataPromise = this.getSeriesFundsList(this.selectedClient.id, this.selectedSeries.id)
        .then((response) => {
          this.originalFundsList = response.data || [];
          let fundsToSort = this.originalFundsList;
          if (this.selectedFund) {
            fundsToSort = concat(this.originalFundsList, this.selectedFund);
          }
          this.selectedFundsList = sortBy(fundsToSort, 'displayName');
        })
        .catch((error) => {
          this.notifyError({
            error,
            section: 'lifecycle:created',
            data: { selectedClient: this.selectedClient, selectedSeries: this.selectedSeries },
            notificaction: {
              title: 'Series Funds List',
              message: `Fetching the series funds list process has pesented an error. (Error: ${error.message})`,
            },
          });
        })
        .finally(() => {
          this.setIsLoading(false);
        });
      return dataPromise;
    },
    getSeriesFundsList(clientId, seriesId) {
      return TargetSeries.getSeriesFundsList(clientId, seriesId);
    },
    handleFundRemoved(removedFund) {
      // check if the fund already exists in the db
      const allFunds = [...this.originalFundsList];
      const matchedFunds = allFunds.filter((fund) => fund.id === removedFund.id);
      if (matchedFunds.length) {
        this.removedFundsList.push(removedFund);
      }
      this.selectedFundsList = sortBy(
        this.selectedFundsList.filter((fund) => fund.id !== removedFund.id),
        'displayName',
      );
      this.openNotification({
        title: 'The changes has been made',
        tinted: true,
      });
    },
    handleFundAdded(addedFund) {
      const selectedFundsList = cloneDeep(this.selectedFundsList);
      selectedFundsList.push(addedFund);
      this.selectedFundsList = sortBy(
        uniqBy(selectedFundsList, 'id'),
        'displayName',
      );
    },
    handleMasiveChangeFundSelection(event) {
      if (event.action === 'add-funds-in-current-page') {
        event.data.map((fund) => this.handleFundAdded(fund));
      } else if (event.action === 'remove-funds-in-current-page') {
        event.data.map((fund) => this.handleFundRemoved(fund));
      }
    },
    handleChangeFundSelection(event) {
      if (event.action === 'add-fund') {
        this.handleFundAdded(event.data);
      } else if (event.action === 'remove-fund') {
        this.handleFundRemoved(event.data);
      } else {
        this.handleMasiveChangeFundSelection(event);
      }
    },
  },
  computed: {
    ...mapState({
      isLoading: (state) => state.system.isLoading,
      savedSelectedFundsList: (state) => state.series.savedSelectedFundsList,
      savedOriginalFundsList: (state) => state.series.savedOriginalFundsList,
      savedRemovedFundsList: (state) => state.series.savedRemovedFundsList,
    }),
    selectedSeries() {
      return this.componentData.selectedSeries || { id: 0, seriesName: '' };
    },
    selectedFund() {
      return this.componentData.selectedFund;
    },
    selectedClient() {
      return this.componentData.selectedClient;
    },
    isEmptyTheSavedSelectedFundsList() {
      return Array.isArray(this.savedSelectedFundsList) && this.savedSelectedFundsList.length === 0;
    },
  },
  watch: {
    selectedSeries() {
      this.loadSeriesFunds();
    },
  },
  async beforeDestroy() {
    await this.saveSelectedFundsList(this.selectedFundsList);
    await this.saveOriginalFundsList(this.originalFundsList);
    await this.saveRemovedFundsList(this.removedFundsList);
  },
};
</script>

<style lang="scss" scoped>
.add-funds-to-series-final {
  &__series-info {
    display: flex;
    margin-top: calc(#{$mds-space-6-x} + 2px);
    margin-bottom: $mds-space-4-x;
    align-items: center;
  }

  &__series-info-name {
    @include mds-level-4-heading();
    color: $fc-main-intr-process-header-text-color;
    margin: 0 $mds-space-4-x 0 0;
    display: inline-block;
    max-width: 50%;
    overflow-wrap: break-word;
    color: $fc-main-intr-process-sub-header-text-color;
  }

  &__funds-selection {
    display: flex;
    align-items: flex-start;
  }

  &__filter-funds {
    @include fc-center-flex();
    border: 1px solid $mds-color-neutral-90;
    box-shadow: 0px 13px 21px 0px rgba(219, 219, 219, 0.5);
    font-size: $fc-main-intr-process-header-text-size;
    color: $fc-main-intr-process-sub-header-text-color;
    margin-right: $mds-space-5-x;
    min-height: 500px;
    width: 820px;

    h4 {
      @include mds-level-4-heading();
    }
  }
}

.button__edit-series-name {
  text-decoration: underline;
}
</style>
